import { Chip, ChipColor, ChipSize } from "@outschool/backpack";
import {
  EnrollmentStatusChip_EnrollmentFragment,
  EnrollmentStatusChip_SectionFragment
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { gql } from "@outschool/ui-apollo";
import React from "react";

import * as Enrollment from "../../../shared/Enrollment";
import * as Section from "../../../shared/Section";

type EnrollmentType = Omit<
  EnrollmentStatusChip_EnrollmentFragment,
  "isWithdrawn"
> & {
  isWithdrawn?: boolean;
};

interface EnrollmentStatusChipProps {
  size?: ChipSize;
  enrollment: EnrollmentType;
  section?: EnrollmentStatusChip_SectionFragment;
}

export function EnrollmentStatusChip({
  size,
  enrollment,
  section
}: EnrollmentStatusChipProps) {
  const { t } = useTranslation(
    "client\\components\\Chips\\EnrollmentStatusChip"
  );
  const isSelfPacedInProgress =
    !!section?.is_self_paced && !Section.isComplete(section);
  const isSelfPacedComplete =
    !!section?.is_self_paced && Section.isComplete(section);
  const isPaused = enrollment.isPaused;
  const isWithdrawn =
    enrollment.isWithdrawn || Enrollment.isWithdrawn(enrollment);
  const isStopped =
    Boolean(enrollment.ongoingEndTime) &&
    dayjs(enrollment.ongoingEndTime).isAfter() &&
    !enrollment.pauseWindow &&
    !Enrollment.isWithdrawn(enrollment);
  const isEnded =
    enrollment.isEnded &&
    Enrollment.endedBeforeSectionEnded(enrollment, section);
  const isComplete =
    enrollment.isEnded &&
    !Enrollment.endedBeforeSectionEnded(enrollment, section);

  const sectionIsComplete = Section.isComplete(section!);
  const sectionIsCanceled = Section.isCanceled(section!);
  let label: string;
  let color: ChipColor;

  if (isSelfPacedComplete || isComplete || sectionIsComplete) {
    label = t("Completed");
    color = "neutral";
  } else if (isPaused) {
    label =
      enrollment.pauseWindow?.pauseReason === "payment_failure"
        ? t("Paused: Payment Issue")
        : t("Paused");
    color = "warning";
  } else if (isWithdrawn) {
    label = t("Withdrawn");
    color = "error";
  } else if (isStopped || isEnded) {
    label = t("Subscription Ended");
    color = "info";
  } else if (sectionIsCanceled) {
    label = t("Canceled");
    color = "error";
  } else if (isSelfPacedInProgress) {
    label = t("In Progress");
    color = "success";
  } else {
    label = t("Enrolled");
    color = "primary";
  }

  return <Chip label={label} size={size} color={color} />;
}

EnrollmentStatusChip.fragments = {
  enrollment: gql`
    fragment EnrollmentStatusChip_enrollment on Enrollment {
      uid
      isPaused
      isEnded
      ongoingEndTime
      withdrawnAt
      isWithdrawn
      pauseWindow {
        uid
        pauseReason
      }
    }
  `,
  section: gql`
    fragment EnrollmentStatusChip_section on Section {
      uid
      is_self_paced
      end_time
      start_time
      deleted_at
      canceled_at
      published_at
    }
  `
};
